import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { FaFolder, FaInfoCircle, FaKey } from 'react-icons/fa';
import CardHeader from '../../../components/Card/CardHeader';
import Modal from '../../../components/Modal/Modal';
import Table from '../../../components/Table/Table';
import Loader from '../../../components/Loader/Loader';
import Pagination from '../../../components/Pagination/Pagination';
import SurveyFolderForm from './SurveyFolderForm';
import FolderStatusPill from './FolderStatusPill';
import { getSurveyFolders, createSurveyFolder } from '../../../services/SurveyFolderService';
import '../../dashboard/Dashboard.css';
import './SurveyFolders.css';
import Button from '../../../components/Button/Button';

const SurveyFolders = () => {
  const permissions = JSON.parse(localStorage.getItem('permissions'));
  const [folders, setFolders] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');

  const fetchFolders = useCallback(async ({ selected = 0 } = {}) => {
    try {
      setIsLoader(true);
      setCurrentPage(selected);
      const response = await getSurveyFolders({ page: selected + 1 });
      if (response.status === 200) {
        setLastPage(response.data.last_page || 1);
        setFolders(response.data.data || []);
      }
    } catch (error) {
      console.error('API Error:', error.response);
    } finally {
      setIsLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders({ selected: 0 });
  }, [fetchFolders]);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setName('');
    setDescription('');
    setNameError('');
  };

  const changeHandler = (e) => {
    const { name: field, value } = e.target;
    if (field === 'name') {
      setName(value);
      setNameError('');
    } else if (field === 'description') {
      setDescription(value);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError('Folder name is required.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await createSurveyFolder({
        name: name.trim(),
        description: description.trim(),
        status: 1,
      });
      if (response.status === 200 || response.status === 201) {
        enqueueSnackbar(response.data.message.success, { variant: 'success' });
        closeModal();
        fetchFolders({ selected: 0 });
      } else {
        enqueueSnackbar(response.data.message.error, { variant: 'error' });
      }
    } catch (error) {
      console.error('API Error:', error.response);
      if (error.response?.status === 422) {
        setNameError(error.response.data?.message?.errors?.name?.[0] || 'This name does not resolve on SurveyMonkey.');
      } else {
        enqueueSnackbar('Something went wrong', { variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const cols = useMemo(() => ([
    {
      title: 'Folder',
      key: 'name',
      sortValue: (row) => row.name,
      render: (row) => (
        <div>
          <div className="folder-table-name">{row.name}</div>
          {row.description && <div className="folder-table-desc">{row.description}</div>}
        </div>
      ),
    },
    {
      title: 'Resolves to',
      render: (row) => (
        row.resolved
          ? <FolderStatusPill tone="success">{row.sm_folder_id}</FolderStatusPill>
          : <FolderStatusPill tone="danger">Unresolved</FolderStatusPill>
      ),
    },
    {
      title: 'Surveys',
      render: (row) => `${row.open_surveys ?? 0} open / ${row.total_in_folder ?? 0}`,
    },
    {
      title: 'Status',
      render: (row) => (
        Number(row.status) === 1
          ? <FolderStatusPill tone="success">Active</FolderStatusPill>
          : <FolderStatusPill tone="muted">Inactive</FolderStatusPill>
      ),
    },
  ]), []);

  return (
    <div className="animated survey-folders-page">
      <div className="card">
        <CardHeader
          title="Survey Folders"
          icon={<FaFolder />}
          button={permissions && permissions.includes('survey.create') &&
            <Button type="submit" variant="primary" onClick={openModal}>Add</Button>
          }
        />
        <div className="card-body">
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <h5>Add Folder</h5>
            <SurveyFolderForm
              changeHandler={changeHandler}
              submitHandler={submitHandler}
              name={name}
              description={description}
              nameError={nameError}
              disabled={isSaving}
            />
          </Modal>

          {isLoader ? <Loader /> : (
            <div className="row mt-3">
              <Table cols={cols} data={folders} hover={true} />
              <Pagination
                totalPage={lastPage}
                currentPage={currentPage}
                handlePageChange={(i) => fetchFolders(i)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyFolders;
